import type { RouteRecordRaw } from 'vue-router'

import { MCP_ENABLE_ROUTE_NAME, MCP_HOME_ROUTE_NAME } from '../routeNames'

import { registerCreditMessages } from '@/features/credits'
import { registerIntegrationsMessages } from '@/features/integrations'
import { registerWorkspaceSettingsMessages } from '@/features/workspace-settings'
import { useLocaleStore } from '@/shared/i18n'

/**
 * The MCP-only product surface (ORP-21466) — four screens under `/mcp` on app.orphex.co.
 *
 * ⚠ NOT on `mcp.orphex.co`. The 2026-08-23 decision (ORP-21473/INFRA-16) that made that host the
 * marketing site only is superseded by ORP-21458/ORP-21828/ORP-21831: it now serves the standalone
 * MCP product shell, consent at `/connect` included, from its own repo (`mcp_orphex_front`). These
 * four screens stay here regardless. `/mcp` was empty at the top level before this record, so
 * nothing is shadowed.
 *
 * The shell composes ALL FOUR children here rather than letting each owning feature contribute
 * its own `routes.ts` entry, because the names and paths are a frozen contract that waves 2 and 3
 * fill in without touching the registration: a page moving between features must not move a URL.
 * The components are the only thing those waves own.
 *
 * `meta.mcpAllowed` rides on the PARENT and reaches every child through vue-router's matched-meta
 * merge, so the MCP redirect branch (ORP-21656) can never bounce a user off their own shell.
 * `meta.requiresMcp` rides along the same way and closes the OTHER direction (ORP-21836): these
 * four screens are the MCP product's shell, so an account that does not hold that product is sent
 * to `mcpEnableRoutes` below instead of browsing another product's chrome.
 */
export const mcpShellRoutes: RouteRecordRaw[] = [
  {
    path: '/mcp',
    redirect: { name: MCP_HOME_ROUTE_NAME },
    meta: { layout: 'mcp', requiresAuth: true, mcpAllowed: true, requiresMcp: true },
    children: [
      {
        path: 'ad-accounts',
        name: MCP_HOME_ROUTE_NAME,
        component: () => import('@/features/integrations/components/mcp/McpAdAccountsPage.vue'),
        meta: { navKey: 'mcp.adAccounts', titleKey: 'integrations.mcp.adAccountsPage.title' },
        beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
      },
      {
        path: 'assistants',
        name: 'McpAssistants',
        component: () => import('@/features/integrations/components/mcp/McpAssistantsPage.vue'),
        meta: { navKey: 'mcp.assistants', titleKey: 'integrations.mcp.assistantsPage.title' },
        beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
      },
      {
        path: 'users',
        name: 'McpUsers',
        component: () => import('@/features/workspace-settings/components/mcp/McpUsersPage.vue'),
        meta: { navKey: 'mcp.users', titleKey: 'workspaceSettings.mcp.users.title' },
        beforeEnter: () => registerWorkspaceSettingsMessages(useLocaleStore().locale),
      },
      {
        path: 'plan',
        name: 'McpPlanBilling',
        component: () => import('@/features/credits/components/mcp/McpPlanBillingPage.vue'),
        meta: { navKey: 'mcp.plan', titleKey: 'credits.mcp.planBilling.title' },
        beforeEnter: () => registerCreditMessages(useLocaleStore().locale),
      },
    ],
  },
]

/**
 * The web product's answer to an `/mcp` deep link it does not own (ORP-21836).
 *
 * A top-level record rather than a fifth child of `/mcp`: a child would inherit `layout: 'mcp'`,
 * `mcpAllowed` and `requiresMcp` and need all three negated, and this page is the opposite of a
 * shell screen — it is the WEB product explaining the MCP one. Same shape as the other top-level
 * records that own their layout outright (`slackLinkRoutes`, `billingReturnRoutes`). `/mcp` matches
 * its own path only, and none of its four children is `enable`, so nothing here is shadowed.
 *
 * Two flags are absent on purpose. No `requiresMcp`, or the branch that redirects here would fire
 * on its own destination. No `mcpAllowed`, so an MCP-only account that types this URL is returned
 * to its shell by the forward branch — it already holds the product this page offers to enable.
 */
export const mcpEnableRoutes: RouteRecordRaw[] = [
  {
    path: '/mcp/enable',
    name: MCP_ENABLE_ROUTE_NAME,
    component: () => import('@/features/integrations/components/mcp/McpEnablePage.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'integrations.mcp.enable.title' },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
]
